<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import DecisionEngineNav from '@/V2/decision-engine/DecisionEngineNav.vue';
import DecisionEngineCardEdit from '@/V2/decision-engine/DecisionEngineCardEdit.vue';
import DecisionEngineRuleEditor from '@/V2/decision-engine/DecisionEngineRuleEditor.vue';
import CheckDelete from '@/V2/decision-engine/checks/CheckDelete.vue';
import {
  iButton,
  iButtonStyles,
  iButtonTypes,
  iButtonSizes,
  iSwitch,
  iTooltip,
  iTooltipAlignmentMode,
  showToast,
} from '@inscribe/signal';
import { PRIVATE_INTERNAL_CHECKS } from '@/constants/privateInternalChecks';

export default {
  components: {
    CheckDelete,
    DecisionEngineNav,
    DecisionEngineCardEdit,
    DecisionEngineRuleEditor,
    iButton,
    iSwitch,
    iTooltip,
  },
  data() {
    return {
      iButtonStyles,
      iButtonTypes,
      iButtonSizes,
      iTooltipAlignmentMode,
      showNewRuleModal: false,
      canBePublic: null,
      savedTimes: 1,
      checkToBeDeleted: null,
      checkPublicValue: null,
      checkRequiredForCollectValue: null,
      triggersAreValid: false,
      requirementsAreValid: false,
      check: null,
    };
  },
  computed: {
    isNew() {
      return this.savedTimes && !this.check.id;
    },
    hasTriggers() {
      return this.check?.triggers?.length > 0;
    },
    hasRequirements() {
      return this.check?.requirements?.length > 0;
    },
    tooltipTitle() {
      if (!this.canBePublic) {
        return 'Cannot display this check to customers';
      }
      if (!this.checkPublicValue) {
        return 'Cannot require this check';
      }
      return '';
    },
    tooltipDescription() {
      if (!this.canBePublic) {
        return 'You cannot show this check to customers because of the variables you have selected';
      }
      if (!this.checkPublicValue) {
        return "You must first enable 'Display During Collect Session' to make this check required";
      }
      return '';
    },
  },
  mounted() {
    this.check = this.getCurrentEditingItem();
    if (!this.check) {
      this.goToChecksList();
    } else {
      this.checkPublicValue = this.check.public;
      this.checkRequiredForCollectValue = this.check.required_for_collect;
    }
    this.setCanBePublic();
  },
  methods: {
    ...mapActions(['createCheck', 'deleteCheck', 'saveCheck']),
    ...mapGetters(['getCurrentEditingItem']),
    ...mapMutations({ updateCheck: 'updateCheck' }),
    goToChecksList() {
      this.$router.push({
        name: 'decision-engine-checks',
      });
    },
    setCanBePublic() {
      this.canBePublic = true;
      if (this.check) {
        const rules = [
          ...(this.check.requirements || []),
          ...(this.check.triggers || []),
        ];
        rules.forEach((requirement) => {
          if (PRIVATE_INTERNAL_CHECKS.includes(requirement.name)) {
            this.canBePublic = false;
            this.checkPublicValue = false;
            this.checkRequiredForCollectValue = false;
            this.updateCheck({
              check: this.check,
              data: {
                public: false,
                required_for_collect: false,
              },
            });
          }
        });
      }
    },
    onBackButtonClick() {
      this.goToChecksList();
    },
    onNavButtonClick() {
      if (this.isNew) {
        this.createCheck(this.check)
          .then((resp) => {
            this.updateCheck({
              check: this.check,
              data: resp,
            });
            showToast({
              text: 'Document check created',
            });
            this.savedTimes++;
            this.goToChecksList();
          })
          .catch((error) => {
            showToast({
              text: error?.response?.data?.message || 'Please try again later.',
              isError: true,
            });
          });
      } else {
        this.saveCheck(this.check)
          .then((resp) => {
            showToast({
              text: 'Document check updated',
            });
            this.savedTimes++;
            this.goToChecksList();
          })
          .catch((error) => {
            showToast({
              text: error?.response?.data?.message || 'Please try again later.',
              isError: true,
            });
          });
      }
    },
    onCheckDeleted() {
      this.goToChecksList();
    },
    onDelete() {
      this.checkToBeDeleted = this.check;
    },
    onNameChange(value) {
      this.updateCheck({
        check: this.check,
        data: {
          name: value,
        },
      });
    },
    onPublicSwitchChange(value) {
      this.updateCheck({
        check: this.check,
        data: {
          public: value,
        },
      });
      // If public is disabled, also disable required_for_collect
      if (!value) {
        this.checkRequiredForCollectValue = false;
        this.onRequiredForCollectSwitchChange(false);
      }
    },
    onRequiredForCollectSwitchChange(value) {
      this.updateCheck({
        check: this.check,
        data: {
          required_for_collect: value,
        },
      });
    },
    onRuleChange(value, type) {
      const dataset = this.check[type];
      dataset[value.index] = value.rule;
      const data = {};
      data[type] = dataset;
      this.updateCheck({
        check: this.check,
        data: data,
      });
      this.setCanBePublic();
      this.setValidFlags();
    },
    setValidFlags() {
      let triggersValue = true;
      let requirementsValue = true;
      this.check.triggers.forEach((trigger) => {
        if (trigger.isValid === false) {
          triggersValue = false;
        }
      });
      this.check.requirements.forEach((requirement) => {
        if (requirement.isValid === false) {
          requirementsValue = false;
        }
      });
      this.triggersAreValid = triggersValue;
      this.requirementsAreValid = requirementsValue;
    },
    onRuleDelete(value, type) {
      const data = {};
      data[type] = [
        ...this.check[type].slice(0, value.index),
        ...this.check[type].slice(value.index + 1),
      ];

      this.updateCheck({
        check: this.check,
        data,
      });
      this.setValidFlags();
    },
    createRule(type) {
      const data = {};
      data[type] = [
        ...this.check[type],
        {
          name: 'document_type',
          operator: 'not_one_of',
          value: undefined,
        },
      ];
      this.updateCheck({
        check: this.check,
        data,
      });
    },
  },
};
</script>

<template>
  <div v-if="check" class="checks-edit edit-container d-flex flex-column">
    <decision-engine-nav
      class="edit-nav"
      category-name="Document Checks"
      :action-name="isNew ? 'New Check' : 'Edit Check'"
      :button-label="isNew ? 'Create' : 'Update'"
      :button-disabled="!triggersAreValid || !requirementsAreValid"
      :item-name="check.name"
      :toggle-value="check.enabled"
      @on-back-button-click="onBackButtonClick"
      @on-name-change="onNameChange"
      @on-delete="onDelete"
      @on-button-click="onNavButtonClick"
    ></decision-engine-nav>
    <div class="content d-flex flex-column i-mt-10 i-gap-9">
      <!-- document filters -->
      <decision-engine-card-edit
        title="Apply Document Filter"
        subtitle="Add a filter to only apply this check to a subset of documents"
      >
        <template #content>
          <div
            :class="{ 'i-mt-9': hasTriggers }"
            class="triggers-edit d-flex flex-column"
            data-testid="check-triggers"
          >
            <decision-engine-rule-editor
              v-for="(trigger, idx) of check.triggers"
              :key="idx + '-' + trigger.name"
              :rule="trigger"
              :item-index="idx"
              @on-rule-change="(value) => onRuleChange(value, 'triggers')"
              @on-rule-delete="(value) => onRuleDelete(value, 'triggers')"
            >
            </decision-engine-rule-editor>
            <i-button
              v-if="hasTriggers"
              text="And"
              class="add-button"
              :type="iButtonTypes.NEUTRAL"
              :size="iButtonSizes.SMALL"
              :button-style="iButtonStyles.SOLID_LIGHT"
              data-testid="add-trigger"
              :disabled="!triggersAreValid"
              @button-click="createRule('triggers')"
            ></i-button>
          </div>
        </template>
        <template #right-side>
          <i-button
            v-if="!hasTriggers"
            text="Add filter"
            class="add-first-button"
            size="xs"
            type="neutral"
            subtype="outline"
            data-testid="add-trigger"
            @button-click="createRule('triggers')"
          ></i-button>
        </template>
      </decision-engine-card-edit>

      <!-- requirements -->
      <decision-engine-card-edit
        title="Set Document Requirements"
        subtitle="Define the requirements to look for in each eligible document"
      >
        <template #content>
          <div
            :class="{ 'i-mt-9': hasRequirements }"
            class="requirements-edit d-flex flex-column"
            data-testid="check-requirements"
          >
            <decision-engine-rule-editor
              v-for="(requirement, idx) of check.requirements"
              :key="idx + '-' + requirement.name"
              :rule="requirement"
              :item-index="idx"
              @on-rule-change="(value) => onRuleChange(value, 'requirements')"
              @on-rule-delete="(value) => onRuleDelete(value, 'requirements')"
            ></decision-engine-rule-editor>

            <i-button
              v-if="hasRequirements"
              text="And"
              class="add-button"
              :type="iButtonTypes.NEUTRAL"
              :size="iButtonSizes.SMALL"
              :button-style="iButtonStyles.SOLID_LIGHT"
              data-testid="add-requirement"
              :disabled="!requirementsAreValid"
              @button-click="createRule('requirements')"
            ></i-button>
          </div>
        </template>
        <template #right-side>
          <i-button
            v-if="!hasRequirements"
            text="Add requirement"
            class="add-first-button"
            size="xs"
            type="neutral"
            subtype="outline"
            data-testid="add-requirement"
            @button-click="createRule('requirements')"
          >
          </i-button>
        </template>
      </decision-engine-card-edit>

      <!-- collect enabled -->
      <decision-engine-card-edit
        title="Display During Collect Session"
        subtitle="Make this check visible to customers during a Collect session"
      >
        <template #right-side>
          <i-switch
            :id="'check-edit-public-switch-' + check.id"
            v-model="checkPublicValue"
            size="sm"
            :disabled="!canBePublic"
            data-testid="check-edit-public-switch"
            @input="onPublicSwitchChange"
          />
          <i-tooltip
            v-if="!canBePublic"
            title="Cannot display this check to customers"
            description="You cannot show this check to customers because of the variables you have selected"
            :alignment="iTooltipAlignmentMode.TOP_MIDDLE"
            :target="'#check-edit-public-switch-' + check.id"
          ></i-tooltip>
        </template>
      </decision-engine-card-edit>

      <!-- required for collect -->
      <decision-engine-card-edit
        title="Required For Collect Session"
        subtitle="Prevent the user from completing a Collect Session until this check passes"
      >
        <template #right-side>
          <i-switch
            :id="'check-edit-required-for-collect-switch-' + check.id"
            v-model="checkRequiredForCollectValue"
            size="sm"
            :disabled="!canBePublic || !checkPublicValue"
            data-testid="check-edit-required-for-collect-switch"
            @input="onRequiredForCollectSwitchChange"
          />
          <i-tooltip
            v-if="!canBePublic || !checkPublicValue"
            :title="tooltipTitle"
            :description="tooltipDescription"
            :alignment="iTooltipAlignmentMode.TOP_MIDDLE"
            :target="'#check-edit-required-for-collect-switch-' + check.id"
          ></i-tooltip>
        </template>
      </decision-engine-card-edit>

      <!-- check delete -->
      <check-delete
        :check-to-be-deleted="checkToBeDeleted"
        @on-close="checkToBeDeleted = null"
        @on-deleted="onCheckDeleted"
      ></check-delete>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.checks-edit {
  margin-top: 50px;

  .decision-engine-rule-setter {
    &:not(:last-of-type) {
      &::after {
        content: 'And';
        color: $light-neutral-120;
        font-size: 12px;
        font-weight: 500;
        margin-top: 12px;
        margin-bottom: 12px;
      }
    }

    &:last-of-type {
      margin-bottom: 20px;
    }
  }

  .content {
    .add-button {
      width: 60px;
    }
  }
}
</style>
